import { parseSlideBlockGroupId } from './slideBlockGroups';
import type {
  ProgramSlideTableBlockV1,
  ProgramSlideTableCellV1,
  ProgramSlideTableTextAlignV1,
  ProgramSlideTableVerticalAlignV1,
} from '../types/programContentSlideCanvas';
import { normalizeSlideTextFontFamily } from '../types/programContentSlideCanvas';

export type TableCellCoord = { row: number; col: number };

export type TableSelectionRect = {
  rowStart: number;
  colStart: number;
  rowEnd: number;
  colEnd: number;
};

export const TABLE_MAX_ROWS = 20;
export const TABLE_MAX_COLS = 10;
export const TABLE_DEFAULT_BORDER_WIDTH_PX = 1;
export const TABLE_DEFAULT_FONT_SIZE_PX = 14;
export const TABLE_HEADER_BG_HEX = '#f0f0f0';

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function clampDim(n: number): number {
  if (!Number.isFinite(n)) return 0.1;
  return Math.min(1, Math.max(0.02, n));
}

function normalizeHexColor(raw: unknown, fallback = '#111111'): string {
  const s = String(raw ?? '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export function normalizeTableTextAlign(raw: unknown): ProgramSlideTableTextAlignV1 {
  if (raw === 'left' || raw === 'center' || raw === 'right') return raw;
  return 'left';
}

export function normalizeTableVerticalAlign(raw: unknown): ProgramSlideTableVerticalAlignV1 {
  if (raw === 'top' || raw === 'middle' || raw === 'bottom') return raw;
  return 'middle';
}

export function isGridSlotInSelection(row: number, col: number, selection: TableSelectionRect | null): boolean {
  if (!selection) return false;
  const rowStart = Math.min(selection.rowStart, selection.rowEnd);
  const rowEnd = Math.max(selection.rowStart, selection.rowEnd);
  const colStart = Math.min(selection.colStart, selection.colEnd);
  const colEnd = Math.max(selection.colStart, selection.colEnd);
  return row >= rowStart && row <= rowEnd && col >= colStart && col <= colEnd;
}

export type TableAnchorSelectionVisual = {
  selected: boolean;
  selectedSingle: boolean;
  selectedMulti: boolean;
  edgeTop: boolean;
  edgeRight: boolean;
  edgeBottom: boolean;
  edgeLeft: boolean;
};

export function getTableAnchorSelectionVisual(
  table: ProgramSlideTableBlockV1,
  anchorRow: number,
  anchorCol: number,
  rowSpan: number,
  colSpan: number,
  selection: TableSelectionRect | null
): TableAnchorSelectionVisual {
  const empty: TableAnchorSelectionVisual = {
    selected: false,
    selectedSingle: false,
    selectedMulti: false,
    edgeTop: false,
    edgeRight: false,
    edgeBottom: false,
    edgeLeft: false,
  };
  if (!selection) return empty;

  const rowStart = Math.min(selection.rowStart, selection.rowEnd);
  const rowEnd = Math.max(selection.rowStart, selection.rowEnd);
  const colStart = Math.min(selection.colStart, selection.colEnd);
  const colEnd = Math.max(selection.colStart, selection.colEnd);
  const ar0 = anchorRow;
  const ar1 = anchorRow + rowSpan - 1;
  const ac0 = anchorCol;
  const ac1 = anchorCol + colSpan - 1;

  if (ar1 < rowStart || ar0 > rowEnd || ac1 < colStart || ac0 > colEnd) return empty;

  let intersects = false;
  for (let r = ar0; r <= ar1 && !intersects; r += 1) {
    for (let c = ac0; c <= ac1; c += 1) {
      if (isGridSlotInSelection(r, c, selection)) {
        intersects = true;
        break;
      }
    }
  }
  if (!intersects) return empty;

  const uniqueAnchorCount = getUniqueAnchorSlotsInSelection(table, selection).length;
  const selectionMatchesAnchor =
    rowStart === ar0 && colStart === ac0 && rowEnd === ar1 && colEnd === ac1;
  const selectedGridCellCount = (rowEnd - rowStart + 1) * (colEnd - colStart + 1);
  const selectedSingle = uniqueAnchorCount === 1 && (selectionMatchesAnchor || selectedGridCellCount === 1);
  const selectedMulti = uniqueAnchorCount > 1;

  if (selectedSingle) {
    return {
      selected: true,
      selectedSingle: true,
      selectedMulti: false,
      edgeTop: false,
      edgeRight: false,
      edgeBottom: false,
      edgeLeft: false,
    };
  }

  if (!selectedMulti) return empty;

  const edgeTop = ar0 === rowStart;
  const edgeBottom = ar1 === rowEnd;
  const edgeLeft = ac0 === colStart;
  const edgeRight = ac1 === colEnd;

  return { selected: true, selectedSingle: false, selectedMulti: true, edgeTop, edgeRight, edgeBottom, edgeLeft };
}

export function selectionRectFromEndpoints(
  rowA: number,
  colA: number,
  rowB: number,
  colB: number
): TableSelectionRect {
  return normalizeTableSelectionRect({
    rowStart: rowA,
    colStart: colA,
    rowEnd: rowB,
    colEnd: colB,
  });
}

export function tableCellKey(row: number, col: number): string {
  return `${row},${col}`;
}

export function genSlideTableId(idx: number): string {
  try {
    const c = globalThis.crypto?.randomUUID?.();
    if (c) return `tbl:${c}`;
  } catch {
    /* ignore */
  }
  return `tbl:${Date.now().toString(36)}:${idx}:${Math.random().toString(36).slice(2, 8)}`;
}

export function genSlideTableCellId(tableId: string, row: number, col: number): string {
  return `${tableId}:c:${row}:${col}`;
}

function equalFracs(count: number): number[] {
  const frac = 1 / count;
  return Array.from({ length: count }, () => frac);
}

export function createDefaultTableCell(
  tableId: string,
  row: number,
  col: number,
  opts?: {
    text?: string;
    textAlign?: ProgramSlideTableTextAlignV1;
    verticalAlign?: ProgramSlideTableVerticalAlignV1;
    colorHex?: string;
    backgroundColorHex?: string;
    bold?: boolean;
  }
): ProgramSlideTableCellV1 {
  return {
    id: genSlideTableCellId(tableId, row, col),
    text: opts?.text ?? '',
    textAlign: opts?.textAlign ?? 'left',
    verticalAlign: opts?.verticalAlign ?? 'middle',
    colorHex: opts?.colorHex ?? '#111111',
    backgroundColorHex: opts?.backgroundColorHex ?? '#ffffff',
    bold: opts?.bold ?? false,
    rowSpan: 1,
    colSpan: 1,
  };
}

export function createTableBlock(input: {
  rowCount: number;
  colCount: number;
  headerRow?: boolean;
  borderWidthPx?: number;
  defaultFontSizePx?: number;
  defaultTextColorHex?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  zIndex?: number;
}): ProgramSlideTableBlockV1 {
  const rowCount = Math.min(TABLE_MAX_ROWS, Math.max(1, Math.round(input.rowCount)));
  const colCount = Math.min(TABLE_MAX_COLS, Math.max(1, Math.round(input.colCount)));
  const id = genSlideTableId(0);
  const headerRow = !!input.headerRow;
  const cells: ProgramSlideTableCellV1[] = [];
  for (let row = 0; row < rowCount; row += 1) {
    for (let col = 0; col < colCount; col += 1) {
      const isHeader = headerRow && row === 0;
      cells.push(
        createDefaultTableCell(id, row, col, {
          textAlign: isHeader ? 'center' : 'left',
          colorHex: input.defaultTextColorHex ?? '#111111',
          backgroundColorHex: isHeader ? TABLE_HEADER_BG_HEX : '#ffffff',
          bold: isHeader,
        })
      );
    }
  }
  return {
    id,
    x: input.x ?? 0.1,
    y: input.y ?? 0.2,
    w: input.w ?? 0.8,
    h: input.h ?? 0.35,
    rowCount,
    colCount,
    cells,
    coveredBy: {},
    rowHeightsFrac: equalFracs(rowCount),
    colWidthsFrac: equalFracs(colCount),
    borderColorHex: '#333333',
    borderWidthPx: input.borderWidthPx ?? TABLE_DEFAULT_BORDER_WIDTH_PX,
    defaultFontSizePx: input.defaultFontSizePx ?? TABLE_DEFAULT_FONT_SIZE_PX,
    defaultFontFamily: 'pretendard',
    headerRow,
    zIndex: input.zIndex ?? 0,
  };
}

export function getTableCellIndex(table: ProgramSlideTableBlockV1, row: number, col: number): number {
  return row * table.colCount + col;
}

export function getTableCellAt(table: ProgramSlideTableBlockV1, row: number, col: number): ProgramSlideTableCellV1 | null {
  if (row < 0 || col < 0 || row >= table.rowCount || col >= table.colCount) return null;
  const idx = getTableCellIndex(table, row, col);
  return table.cells[idx] ?? null;
}

export function isTableCellCovered(table: ProgramSlideTableBlockV1, row: number, col: number): boolean {
  const key = tableCellKey(row, col);
  return !!(table.coveredBy && table.coveredBy[key]);
}

export function getTableAnchorCell(
  table: ProgramSlideTableBlockV1,
  row: number,
  col: number
): { cell: ProgramSlideTableCellV1; anchorRow: number; anchorCol: number } | null {
  if (row < 0 || col < 0 || row >= table.rowCount || col >= table.colCount) return null;
  const covered = table.coveredBy?.[tableCellKey(row, col)];
  if (covered) {
    for (let r = 0; r < table.rowCount; r += 1) {
      for (let c = 0; c < table.colCount; c += 1) {
        const cell = getTableCellAt(table, r, c);
        if (cell && cell.id === covered) {
          return { cell, anchorRow: r, anchorCol: c };
        }
      }
    }
    return null;
  }
  const cell = getTableCellAt(table, row, col);
  if (!cell) return null;
  return { cell, anchorRow: row, anchorCol: col };
}

export function normalizeTableSelectionRect(rect: TableSelectionRect): TableSelectionRect {
  return {
    rowStart: Math.min(rect.rowStart, rect.rowEnd),
    colStart: Math.min(rect.colStart, rect.colEnd),
    rowEnd: Math.max(rect.rowStart, rect.rowEnd),
    colEnd: Math.max(rect.colStart, rect.colEnd),
  };
}

export function selectionRectFromCoords(coords: TableCellCoord[]): TableSelectionRect | null {
  if (!coords.length) return null;
  let rowStart = coords[0].row;
  let rowEnd = coords[0].row;
  let colStart = coords[0].col;
  let colEnd = coords[0].col;
  for (const c of coords) {
    rowStart = Math.min(rowStart, c.row);
    rowEnd = Math.max(rowEnd, c.row);
    colStart = Math.min(colStart, c.col);
    colEnd = Math.max(colEnd, c.col);
  }
  return normalizeTableSelectionRect({ rowStart, colStart, rowEnd, colEnd });
}

export function isRectangularSelection(table: ProgramSlideTableBlockV1, rect: TableSelectionRect): boolean {
  const r = normalizeTableSelectionRect(rect);
  for (let row = r.rowStart; row <= r.rowEnd; row += 1) {
    for (let col = r.colStart; col <= r.colEnd; col += 1) {
      const anchor = getTableAnchorCell(table, row, col);
      if (!anchor) return false;
      const { cell, anchorRow, anchorCol } = anchor;
      const spanRowEnd = anchorRow + cell.rowSpan - 1;
      const spanColEnd = anchorCol + cell.colSpan - 1;
      if (anchorRow < r.rowStart || anchorCol < r.colStart || spanRowEnd > r.rowEnd || spanColEnd > r.colEnd) {
        return false;
      }
    }
  }
  return true;
}

export type TableCellOperationResult = {
  table: ProgramSlideTableBlockV1;
  selection: TableSelectionRect;
  error?: string;
};

export function getUniqueAnchorSlotsInSelection(
  table: ProgramSlideTableBlockV1,
  rect: TableSelectionRect
): { row: number; col: number }[] {
  const r = normalizeTableSelectionRect(rect);
  const slots: { row: number; col: number }[] = [];
  const seen = new Set<string>();
  for (let row = r.rowStart; row <= r.rowEnd; row += 1) {
    for (let col = r.colStart; col <= r.colEnd; col += 1) {
      const anchor = getTableAnchorCell(table, row, col);
      if (!anchor) continue;
      const key = tableCellKey(anchor.anchorRow, anchor.anchorCol);
      if (seen.has(key)) continue;
      seen.add(key);
      slots.push({ row: anchor.anchorRow, col: anchor.anchorCol });
    }
  }
  return slots;
}

export function expandSelectionToAnchorBounds(
  table: ProgramSlideTableBlockV1,
  rect: TableSelectionRect
): TableSelectionRect {
  const prepared = normalizeTableGrid(table);
  let next = normalizeTableSelectionRect(rect);
  for (let i = 0; i < 32; i += 1) {
    const rowStart = next.rowStart;
    const rowEnd = next.rowEnd;
    const colStart = next.colStart;
    const colEnd = next.colEnd;
    let expanded = false;
    for (let row = rowStart; row <= rowEnd; row += 1) {
      for (let col = colStart; col <= colEnd; col += 1) {
        const anchor = getTableAnchorCell(prepared, row, col);
        if (!anchor) continue;
        const ar = anchor.anchorRow;
        const ac = anchor.anchorCol;
        const ar1 = ar + anchor.cell.rowSpan - 1;
        const ac1 = ac + anchor.cell.colSpan - 1;
        if (ar < rowStart || ac < colStart || ar1 > rowEnd || ac1 > colEnd) {
          next = normalizeTableSelectionRect({
            rowStart: Math.min(rowStart, ar),
            colStart: Math.min(colStart, ac),
            rowEnd: Math.max(rowEnd, ar1),
            colEnd: Math.max(colEnd, ac1),
          });
          expanded = true;
          break;
        }
      }
      if (expanded) break;
    }
    if (!expanded) break;
  }
  return next;
}

function anchorFullyInsideSelection(
  table: ProgramSlideTableBlockV1,
  anchorRow: number,
  anchorCol: number,
  rect: TableSelectionRect
): boolean {
  const r = normalizeTableSelectionRect(rect);
  const anchor = getTableAnchorCell(table, anchorRow, anchorCol);
  if (!anchor) return false;
  const { cell } = anchor;
  const spanRowEnd = anchorRow + cell.rowSpan - 1;
  const spanColEnd = anchorCol + cell.colSpan - 1;
  return anchorRow >= r.rowStart && anchorCol >= r.colStart && spanRowEnd <= r.rowEnd && spanColEnd <= r.colEnd;
}

function selectionWithinAnchor(
  table: ProgramSlideTableBlockV1,
  anchorRow: number,
  anchorCol: number,
  rect: TableSelectionRect
): boolean {
  const r = normalizeTableSelectionRect(rect);
  const anchor = getTableAnchorCell(table, anchorRow, anchorCol);
  if (!anchor) return false;
  const { cell } = anchor;
  const spanRowEnd = anchorRow + cell.rowSpan - 1;
  const spanColEnd = anchorCol + cell.colSpan - 1;
  return r.rowStart >= anchorRow && r.colStart >= anchorCol && r.rowEnd <= spanRowEnd && r.colEnd <= spanColEnd;
}

export function selectionRectForAnchor(
  table: ProgramSlideTableBlockV1,
  row: number,
  col: number
): TableSelectionRect | null {
  const anchor = getTableAnchorCell(table, row, col);
  if (!anchor) return null;
  return normalizeTableSelectionRect({
    rowStart: anchor.anchorRow,
    colStart: anchor.anchorCol,
    rowEnd: anchor.anchorRow + anchor.cell.rowSpan - 1,
    colEnd: anchor.anchorCol + anchor.cell.colSpan - 1,
  });
}

function splitCellStyleSource(cell: ProgramSlideTableCellV1) {
  return {
    textAlign: cell.textAlign,
    verticalAlign: cell.verticalAlign,
    colorHex: cell.colorHex,
    backgroundColorHex: cell.backgroundColorHex,
    bold: cell.bold,
  };
}

export function cloneTableBlock(table: ProgramSlideTableBlockV1): ProgramSlideTableBlockV1 {
  return {
    ...table,
    cells: table.cells.map((c) => ({ ...c })),
    rowHeightsFrac: [...table.rowHeightsFrac],
    colWidthsFrac: [...table.colWidthsFrac],
    coveredBy: table.coveredBy ? { ...table.coveredBy } : {},
  };
}

export function normalizeTableGrid(table: ProgramSlideTableBlockV1): ProgramSlideTableBlockV1 {
  const next = cloneTableBlock(table);
  const expected = next.rowCount * next.colCount;
  while (next.cells.length < expected) {
    const idx = next.cells.length;
    const row = Math.floor(idx / next.colCount);
    const col = idx % next.colCount;
    next.cells.push(createDefaultTableCell(next.id, row, col));
  }
  if (next.cells.length > expected) {
    next.cells = next.cells.slice(0, expected);
  }
  while (next.rowHeightsFrac.length < next.rowCount) {
    next.rowHeightsFrac.push(1 / next.rowCount);
  }
  while (next.colWidthsFrac.length < next.colCount) {
    next.colWidthsFrac.push(1 / next.colCount);
  }
  next.rowHeightsFrac = next.rowHeightsFrac.slice(0, next.rowCount);
  next.colWidthsFrac = next.colWidthsFrac.slice(0, next.colCount);
  const rowSum = next.rowHeightsFrac.reduce((a, b) => a + b, 0) || 1;
  const colSum = next.colWidthsFrac.reduce((a, b) => a + b, 0) || 1;
  next.rowHeightsFrac = next.rowHeightsFrac.map((v) => v / rowSum);
  next.colWidthsFrac = next.colWidthsFrac.map((v) => v / colSum);

  const coveredBy: Record<string, string> = {};
  for (let row = 0; row < next.rowCount; row += 1) {
    for (let col = 0; col < next.colCount; col += 1) {
      const key = tableCellKey(row, col);
      if (coveredBy[key]) {
        const coveredCell = getTableCellAt(next, row, col);
        if (coveredCell) {
          coveredCell.rowSpan = 1;
          coveredCell.colSpan = 1;
        }
        continue;
      }

      const cell = getTableCellAt(next, row, col);
      if (!cell) continue;

      let rowSpan = Math.max(1, Math.min(next.rowCount - row, cell.rowSpan || 1));
      let colSpan = Math.max(1, Math.min(next.colCount - col, cell.colSpan || 1));

      while (rowSpan > 1 || colSpan > 1) {
        let blocked = false;
        for (let r = row; r < row + rowSpan && !blocked; r += 1) {
          for (let c = col; c < col + colSpan; c += 1) {
            if (r === row && c === col) continue;
            if (coveredBy[tableCellKey(r, c)]) {
              blocked = true;
              break;
            }
          }
        }
        if (!blocked) break;
        if (colSpan > 1) colSpan -= 1;
        else if (rowSpan > 1) rowSpan -= 1;
        else break;
      }

      cell.rowSpan = rowSpan;
      cell.colSpan = colSpan;
      if (rowSpan === 1 && colSpan === 1) continue;

      for (let r = row; r < row + rowSpan; r += 1) {
        for (let c = col; c < col + colSpan; c += 1) {
          if (r === row && c === col) continue;
          coveredBy[tableCellKey(r, c)] = cell.id;
          const coveredCell = getTableCellAt(next, r, c);
          if (coveredCell) {
            coveredCell.rowSpan = 1;
            coveredCell.colSpan = 1;
          }
        }
      }
    }
  }
  next.coveredBy = coveredBy;
  return next;
}

export function patchTableCells(
  table: ProgramSlideTableBlockV1,
  cellIds: string[],
  patch: Partial<
    Pick<
      ProgramSlideTableCellV1,
      'text' | 'textAlign' | 'verticalAlign' | 'colorHex' | 'backgroundColorHex' | 'bold'
    >
  >
): ProgramSlideTableBlockV1 {
  const idSet = new Set(cellIds);
  const next = cloneTableBlock(table);
  next.cells = next.cells.map((cell) => (idSet.has(cell.id) ? { ...cell, ...patch } : cell));
  return normalizeTableGrid(next);
}

export function clearTableCellContents(table: ProgramSlideTableBlockV1, cellIds: string[]): ProgramSlideTableBlockV1 {
  return patchTableCells(table, cellIds, { text: '' });
}

export function canMergeTableSelection(
  table: ProgramSlideTableBlockV1,
  rect: TableSelectionRect
): boolean {
  const prepared = normalizeTableGrid(table);
  const expanded = expandSelectionToAnchorBounds(prepared, rect);
  if (expanded.rowStart === expanded.rowEnd && expanded.colStart === expanded.colEnd) return false;
  return isRectangularSelection(prepared, expanded);
}

export function mergeTableCells(
  table: ProgramSlideTableBlockV1,
  rect: TableSelectionRect
): TableCellOperationResult {
  const prepared = normalizeTableGrid(table);
  const r = expandSelectionToAnchorBounds(prepared, normalizeTableSelectionRect(rect));
  if (r.rowStart === r.rowEnd && r.colStart === r.colEnd) {
    return { table, selection: r, error: '병합하려면 두 개 이상의 셀을 선택하세요.' };
  }

  if (!isRectangularSelection(prepared, r)) {
    return {
      table: prepared,
      selection: r,
      error: '선택 범위에 걸친 병합 셀이 있습니다. 병합 셀 전체가 선택에 포함되거나, 나뉘지 않은 셀만 선택하세요.',
    };
  }
  const anchor = getTableAnchorCell(prepared, r.rowStart, r.colStart);
  if (!anchor) return { table: prepared, selection: r, error: '병합할 셀을 찾을 수 없습니다.' };

  const next = cloneTableBlock(prepared);
  const anchorIdx = getTableCellIndex(next, r.rowStart, r.colStart);
  const anchorCell = next.cells[anchorIdx];
  if (!anchorCell) return { table: prepared, selection: r, error: '병합할 셀을 찾을 수 없습니다.' };

  const texts: string[] = [];
  for (let row = r.rowStart; row <= r.rowEnd; row += 1) {
    for (let col = r.colStart; col <= r.colEnd; col += 1) {
      const slotAnchor = getTableAnchorCell(next, row, col);
      const text = slotAnchor?.cell.text?.trim() ?? '';
      if (text) texts.push(text);
    }
  }

  const rowSpan = r.rowEnd - r.rowStart + 1;
  const colSpan = r.colEnd - r.colStart + 1;
  anchorCell.rowSpan = rowSpan;
  anchorCell.colSpan = colSpan;
  // 구글 문서: 좌상단(머리) 셀 내용만 표시. 나머지 셀 텍스트는 covered 상태로 보존해 병합 해제 시 복원.
  if (!anchorCell.text?.trim() && texts.length) {
    anchorCell.text = texts.join('\n');
  }

  const coveredBy = { ...(next.coveredBy ?? {}) };
  for (let row = r.rowStart; row <= r.rowEnd; row += 1) {
    for (let col = r.colStart; col <= r.colEnd; col += 1) {
      if (row === r.rowStart && col === r.colStart) {
        delete coveredBy[tableCellKey(row, col)];
        continue;
      }
      coveredBy[tableCellKey(row, col)] = anchorCell.id;
      const idx = getTableCellIndex(next, row, col);
      const cell = next.cells[idx];
      if (cell) {
        cell.rowSpan = 1;
        cell.colSpan = 1;
      }
    }
  }
  next.coveredBy = coveredBy;
  const merged = normalizeTableGrid(next);
  return { table: merged, selection: expandSelectionToAnchorBounds(merged, r) };
}

function collectTableAnchorSlots(
  table: ProgramSlideTableBlockV1
): { row: number; col: number; cell: ProgramSlideTableCellV1 }[] {
  const slots: { row: number; col: number; cell: ProgramSlideTableCellV1 }[] = [];
  const seen = new Set<string>();
  for (let row = 0; row < table.rowCount; row += 1) {
    for (let col = 0; col < table.colCount; col += 1) {
      const anchor = getTableAnchorCell(table, row, col);
      if (!anchor || anchor.anchorRow !== row || anchor.anchorCol !== col) continue;
      if (seen.has(anchor.cell.id)) continue;
      seen.add(anchor.cell.id);
      slots.push({ row, col, cell: anchor.cell });
    }
  }
  return slots;
}

export type TableCellSplitDirection = 'horizontal' | 'vertical';

function splitAnchorWithinExistingColumns(
  table: ProgramSlideTableBlockV1,
  ar: number,
  ac: number,
  rowSpan: number,
  colSpan: number,
  leftColSpan: number,
  rightColSpan: number
): ProgramSlideTableBlockV1 {
  const next = cloneTableBlock(table);
  const anchorIdx = getTableCellIndex(next, ar, ac);
  const anchorCell = next.cells[anchorIdx];
  if (!anchorCell) return table;

  const rightIdx = getTableCellIndex(next, ar, ac + leftColSpan);
  const rightCell = next.cells[rightIdx];
  if (!rightCell) return table;

  anchorCell.colSpan = leftColSpan;
  anchorCell.rowSpan = rowSpan;

  rightCell.rowSpan = rowSpan;
  rightCell.colSpan = rightColSpan;
  rightCell.text = '';
  Object.assign(rightCell, splitCellStyleSource(anchorCell));

  const coveredBy = { ...(next.coveredBy ?? {}) };
  for (let row = ar; row < ar + rowSpan; row += 1) {
    for (let col = ac; col < ac + colSpan; col += 1) {
      delete coveredBy[tableCellKey(row, col)];
      if (row === ar && col === ac) continue;
      if (col >= ac + leftColSpan) {
        coveredBy[tableCellKey(row, col)] = rightCell.id;
        if (!(row === ar && col === ac + leftColSpan)) {
          const idx = getTableCellIndex(next, row, col);
          const coveredCell = next.cells[idx];
          if (coveredCell) {
            coveredCell.rowSpan = 1;
            coveredCell.colSpan = 1;
            coveredCell.text = '';
          }
        }
      }
    }
  }
  next.coveredBy = coveredBy;
  return normalizeTableGrid(next);
}

function splitAnchorWithinExistingRows(
  table: ProgramSlideTableBlockV1,
  ar: number,
  ac: number,
  rowSpan: number,
  colSpan: number,
  topRowSpan: number,
  bottomRowSpan: number
): ProgramSlideTableBlockV1 {
  const next = cloneTableBlock(table);
  const anchorIdx = getTableCellIndex(next, ar, ac);
  const anchorCell = next.cells[anchorIdx];
  if (!anchorCell) return table;

  const bottomIdx = getTableCellIndex(next, ar + topRowSpan, ac);
  const bottomCell = next.cells[bottomIdx];
  if (!bottomCell) return table;

  anchorCell.rowSpan = topRowSpan;
  anchorCell.colSpan = colSpan;

  bottomCell.rowSpan = bottomRowSpan;
  bottomCell.colSpan = colSpan;
  bottomCell.text = '';
  Object.assign(bottomCell, splitCellStyleSource(anchorCell));

  const coveredBy = { ...(next.coveredBy ?? {}) };
  for (let row = ar; row < ar + rowSpan; row += 1) {
    for (let col = ac; col < ac + colSpan; col += 1) {
      delete coveredBy[tableCellKey(row, col)];
      if (row === ar && col === ac) continue;
      if (row >= ar + topRowSpan) {
        coveredBy[tableCellKey(row, col)] = bottomCell.id;
        if (!(row === ar + topRowSpan && col === ac)) {
          const idx = getTableCellIndex(next, row, col);
          const coveredCell = next.cells[idx];
          if (coveredCell) {
            coveredCell.rowSpan = 1;
            coveredCell.colSpan = 1;
            coveredCell.text = '';
          }
        }
      }
    }
  }
  next.coveredBy = coveredBy;
  return normalizeTableGrid(next);
}

function splitRowBandIntersects(
  anchorRow: number,
  rowSpan: number,
  splitRowStart: number,
  splitRowSpan: number
): boolean {
  const splitRowEnd = splitRowStart + splitRowSpan - 1;
  return anchorRow <= splitRowEnd && anchorRow + rowSpan - 1 >= splitRowStart;
}

function splitColBandIntersects(
  anchorCol: number,
  colSpan: number,
  splitColStart: number,
  splitColSpan: number
): boolean {
  const splitColEnd = splitColStart + splitColSpan - 1;
  return anchorCol <= splitColEnd && anchorCol + colSpan - 1 >= splitColStart;
}

function rebuildTableFromAnchors(
  tableId: string,
  rowCount: number,
  colCount: number,
  anchors: { row: number; col: number; cell: ProgramSlideTableCellV1 }[]
): ProgramSlideTableCellV1[] {
  const cells: ProgramSlideTableCellV1[] = [];
  for (let row = 0; row < rowCount; row += 1) {
    for (let col = 0; col < colCount; col += 1) {
      cells.push(createDefaultTableCell(tableId, row, col));
    }
  }
  for (const anchor of anchors) {
    if (anchor.row < 0 || anchor.col < 0 || anchor.row >= rowCount || anchor.col >= colCount) continue;
    cells[anchor.row * colCount + anchor.col] = anchor.cell;
  }
  return cells;
}

function insertColumnForLocalHorizontalSplit(
  table: ProgramSlideTableBlockV1,
  ar: number,
  ac: number,
  rowSpan: number,
  colSpan: number
): ProgramSlideTableBlockV1 {
  const insertAt = ac + colSpan;
  const splitCell = getTableCellAt(table, ar, ac);
  if (!splitCell) return table;

  const next = cloneTableBlock(table);
  const oldColCount = table.colCount;
  const newColCount = oldColCount + 1;
  const splitRowStart = ar;
  const splitColStart = ac;

  const placed: { row: number; col: number; cell: ProgramSlideTableCellV1 }[] = [];
  const processed = new Set<string>();

  for (let row = 0; row < table.rowCount; row += 1) {
    for (let col = 0; col < oldColCount; col += 1) {
      const anchor = getTableAnchorCell(table, row, col);
      if (!anchor || anchor.anchorRow !== row || anchor.anchorCol !== col) continue;
      if (processed.has(anchor.cell.id)) continue;
      processed.add(anchor.cell.id);

      const { cell, anchorRow, anchorCol } = anchor;
      const rs = Math.max(1, cell.rowSpan || 1);
      const cs = Math.max(1, cell.colSpan || 1);
      const spanColEnd = anchorCol + cs - 1;

      if (anchorRow === ar && anchorCol === ac) {
        placed.push({
          row: ar,
          col: ac,
          cell: {
            ...cell,
            id: genSlideTableCellId(next.id, ar, ac),
            rowSpan: rs,
            colSpan,
          },
        });
        placed.push({
          row: ar,
          col: insertAt,
          cell: {
            ...createDefaultTableCell(next.id, ar, insertAt, splitCellStyleSource(cell)),
            id: genSlideTableCellId(next.id, ar, insertAt),
            rowSpan: rs,
            colSpan,
            text: '',
          },
        });
        continue;
      }

      const newCol = anchorCol >= insertAt ? anchorCol + 1 : anchorCol;
      let newCs = cs;
      const endsBeforeInsert = spanColEnd === insertAt - 1;
      const crossesInsert = anchorCol < insertAt && spanColEnd >= insertAt;
      const inSplitRowBand = splitRowBandIntersects(anchorRow, rs, splitRowStart, rowSpan);
      const inSplitColBand = splitColBandIntersects(anchorCol, cs, splitColStart, colSpan);
      const isSplitRowSideNeighbor = inSplitRowBand && !inSplitColBand;
      const mayExtendColSpan = (crossesInsert || endsBeforeInsert) && !isSplitRowSideNeighbor;

      if (mayExtendColSpan) {
        newCs = cs + 1;
      }

      placed.push({
        row: anchorRow,
        col: newCol,
        cell: {
          ...cell,
          id: genSlideTableCellId(next.id, anchorRow, newCol),
          rowSpan: rs,
          colSpan: newCs,
        },
      });
    }
  }

  next.colCount = newColCount;
  next.cells = rebuildTableFromAnchors(next.id, table.rowCount, newColCount, placed);
  const splitWidth = table.colWidthsFrac[ac] ?? 1 / oldColCount;
  const leftWidth = splitWidth / 2;
  const rightWidth = splitWidth / 2;
  next.colWidthsFrac = [...table.colWidthsFrac];
  next.colWidthsFrac.splice(insertAt, 0, rightWidth);
  next.colWidthsFrac[ac] = leftWidth;
  return normalizeTableGrid(next);
}

function insertRowForLocalVerticalSplit(
  table: ProgramSlideTableBlockV1,
  ar: number,
  ac: number,
  rowSpan: number,
  colSpan: number
): ProgramSlideTableBlockV1 {
  const insertAt = ar + rowSpan;
  const splitCell = getTableCellAt(table, ar, ac);
  if (!splitCell) return table;

  const next = cloneTableBlock(table);
  const oldRowCount = table.rowCount;
  const newRowCount = oldRowCount + 1;
  const splitRowStart = ar;
  const splitColStart = ac;

  const placed: { row: number; col: number; cell: ProgramSlideTableCellV1 }[] = [];
  const processed = new Set<string>();

  for (let row = 0; row < oldRowCount; row += 1) {
    for (let col = 0; col < table.colCount; col += 1) {
      const anchor = getTableAnchorCell(table, row, col);
      if (!anchor || anchor.anchorRow !== row || anchor.anchorCol !== col) continue;
      if (processed.has(anchor.cell.id)) continue;
      processed.add(anchor.cell.id);

      const { cell, anchorRow, anchorCol } = anchor;
      const rs = Math.max(1, cell.rowSpan || 1);
      const cs = Math.max(1, cell.colSpan || 1);
      const spanRowEnd = anchorRow + rs - 1;

      if (anchorRow === ar && anchorCol === ac) {
        placed.push({
          row: ar,
          col: ac,
          cell: {
            ...cell,
            id: genSlideTableCellId(next.id, ar, ac),
            rowSpan,
            colSpan: cs,
          },
        });
        placed.push({
          row: insertAt,
          col: ac,
          cell: {
            ...createDefaultTableCell(next.id, insertAt, ac, splitCellStyleSource(cell)),
            id: genSlideTableCellId(next.id, insertAt, ac),
            rowSpan,
            colSpan: cs,
            text: '',
          },
        });
        continue;
      }

      const newRow = anchorRow >= insertAt ? anchorRow + 1 : anchorRow;
      let newRs = rs;
      const endsBeforeInsert = spanRowEnd === insertAt - 1;
      const crossesInsert = anchorRow < insertAt && spanRowEnd >= insertAt;
      const inSplitRowBand = splitRowBandIntersects(anchorRow, rs, splitRowStart, rowSpan);
      const inSplitColBand = splitColBandIntersects(anchorCol, cs, splitColStart, colSpan);
      // 같은 열의 위·아래 이웃만 rowspan 확장 제외 (가로 나누기의 옆 셀 규칙과 대칭)
      const isSplitColSideNeighbor = inSplitColBand && !inSplitRowBand;
      const mayExtendRowSpan = (crossesInsert || endsBeforeInsert) && !isSplitColSideNeighbor;

      if (mayExtendRowSpan) {
        newRs = rs + 1;
      }

      placed.push({
        row: newRow,
        col: anchorCol,
        cell: {
          ...cell,
          id: genSlideTableCellId(next.id, newRow, anchorCol),
          rowSpan: newRs,
          colSpan: cs,
        },
      });
    }
  }

  next.rowCount = newRowCount;
  next.cells = rebuildTableFromAnchors(next.id, newRowCount, table.colCount, placed);
  const splitHeight = table.rowHeightsFrac[ar] ?? 1 / oldRowCount;
  const topHeight = splitHeight / 2;
  const bottomHeight = splitHeight / 2;
  next.rowHeightsFrac = [...table.rowHeightsFrac];
  next.rowHeightsFrac.splice(insertAt, 0, bottomHeight);
  next.rowHeightsFrac[ar] = topHeight;
  return normalizeTableGrid(next);
}

export function splitTableCell(
  table: ProgramSlideTableBlockV1,
  anchorRow: number,
  anchorCol: number,
  direction: TableCellSplitDirection
): { table: ProgramSlideTableBlockV1; error?: string } {
  const anchor = getTableAnchorCell(table, anchorRow, anchorCol);
  if (!anchor) return { table, error: '분할할 셀을 찾을 수 없습니다.' };
  const { cell, anchorRow: ar, anchorCol: ac } = anchor;
  const rowSpan = Math.max(1, cell.rowSpan || 1);
  const colSpan = Math.max(1, cell.colSpan || 1);

  if (direction === 'horizontal') {
    const leftColSpan = Math.floor(colSpan / 2);
    const rightColSpan = colSpan - leftColSpan;
    if (rightColSpan <= 0) return { table, error: '셀을 더 나눌 수 없습니다.' };

    if (colSpan > 1) {
      return { table: splitAnchorWithinExistingColumns(table, ar, ac, rowSpan, colSpan, leftColSpan, rightColSpan) };
    }
    if (table.colCount >= TABLE_MAX_COLS) {
      return { table, error: `열은 최대 ${TABLE_MAX_COLS}개까지입니다.` };
    }
    return { table: insertColumnForLocalHorizontalSplit(table, ar, ac, rowSpan, colSpan) };
  }

  const topRowSpan = Math.floor(rowSpan / 2);
  const bottomRowSpan = rowSpan - topRowSpan;
  if (bottomRowSpan <= 0) return { table, error: '셀을 더 나눌 수 없습니다.' };

  if (rowSpan > 1) {
    return { table: splitAnchorWithinExistingRows(table, ar, ac, rowSpan, colSpan, topRowSpan, bottomRowSpan) };
  }
  if (table.rowCount >= TABLE_MAX_ROWS) {
    return { table, error: `행은 최대 ${TABLE_MAX_ROWS}개까지입니다.` };
  }
  return { table: insertRowForLocalVerticalSplit(table, ar, ac, rowSpan, colSpan) };
}

function canSplitAnchor(
  table: ProgramSlideTableBlockV1,
  anchorRow: number,
  anchorCol: number,
  direction: TableCellSplitDirection
): boolean {
  const anchor = getTableAnchorCell(table, anchorRow, anchorCol);
  if (!anchor) return false;
  const rowSpan = Math.max(1, anchor.cell.rowSpan || 1);
  const colSpan = Math.max(1, anchor.cell.colSpan || 1);
  if (direction === 'horizontal') {
    return colSpan > 1 || table.colCount < TABLE_MAX_COLS;
  }
  return rowSpan > 1 || table.rowCount < TABLE_MAX_ROWS;
}

export function canSplitTableSelection(
  table: ProgramSlideTableBlockV1,
  rect: TableSelectionRect,
  direction: TableCellSplitDirection
): boolean {
  const slots = getUniqueAnchorSlotsInSelection(table, rect);
  if (slots.length !== 1) return false;
  const slot = slots[0];
  return (
    selectionWithinAnchor(table, slot.row, slot.col, rect) &&
    canSplitAnchor(table, slot.row, slot.col, direction)
  );
}

export function splitTableCellsInSelection(
  table: ProgramSlideTableBlockV1,
  rect: TableSelectionRect,
  direction: TableCellSplitDirection
): TableCellOperationResult {
  const r = normalizeTableSelectionRect(rect);
  let next = normalizeTableGrid(table);
  let selection = r;

  const slots = getUniqueAnchorSlotsInSelection(next, r);
  if (slots.length !== 1) {
    return {
      table: next,
      selection: r,
      error: '셀 나누기는 하나의 셀만 선택한 뒤 실행하세요.',
    };
  }

  const slot = slots[0];
  if (!selectionWithinAnchor(next, slot.row, slot.col, selection)) {
    return {
      table: next,
      selection,
      error: '나눌 셀을 선택하세요.',
    };
  }

  if (!canSplitAnchor(next, slot.row, slot.col, direction)) {
    return {
      table: next,
      selection,
      error:
        direction === 'horizontal'
          ? `열은 최대 ${TABLE_MAX_COLS}개까지입니다.`
          : `행은 최대 ${TABLE_MAX_ROWS}개까지입니다.`,
    };
  }

  const anchor = getTableAnchorCell(next, slot.row, slot.col);
  if (!anchor) {
    return { table: next, selection, error: '분할할 셀을 찾을 수 없습니다.' };
  }

  const splitResult = splitTableCell(next, slot.row, slot.col, direction);
  if (splitResult.error) {
    return { table: next, selection, error: splitResult.error };
  }
  next = normalizeTableGrid(splitResult.table);

  const rowSpan = Math.max(1, anchor.cell.rowSpan || 1);
  const colSpan = Math.max(1, anchor.cell.colSpan || 1);
  if (direction === 'horizontal') {
    const leftColSpan = Math.max(1, Math.floor(colSpan / 2));
    selection = normalizeTableSelectionRect({
      rowStart: anchor.anchorRow,
      colStart: anchor.anchorCol,
      rowEnd: anchor.anchorRow + rowSpan - 1,
      colEnd: anchor.anchorCol + leftColSpan - 1,
    });
  } else {
    const topRowSpan = Math.max(1, Math.floor(rowSpan / 2));
    selection = normalizeTableSelectionRect({
      rowStart: anchor.anchorRow,
      colStart: anchor.anchorCol,
      rowEnd: anchor.anchorRow + topRowSpan - 1,
      colEnd: anchor.anchorCol + colSpan - 1,
    });
  }

  return { table: next, selection };
}

function insertTableRowAt(table: ProgramSlideTableBlockV1, insertAt: number): ProgramSlideTableBlockV1 {
  if (table.rowCount >= TABLE_MAX_ROWS) return table;
  if (insertAt < 0 || insertAt > table.rowCount) return table;

  const prepared = normalizeTableGrid(table);
  const next = cloneTableBlock(prepared);
  const placed: { row: number; col: number; cell: ProgramSlideTableCellV1 }[] = [];
  const processed = new Set<string>();

  for (const { row: anchorRow, col: anchorCol, cell } of collectTableAnchorSlots(prepared)) {
    if (processed.has(cell.id)) continue;
    processed.add(cell.id);
    const rs = Math.max(1, cell.rowSpan || 1);
    const cs = Math.max(1, cell.colSpan || 1);

    let newRow = anchorRow;
    let newRs = rs;
    if (anchorRow >= insertAt) {
      newRow = anchorRow + 1;
    } else if (anchorRow + rs > insertAt) {
      newRs = rs + 1;
    }

    placed.push({
      row: newRow,
      col: anchorCol,
      cell: {
        ...cell,
        id: genSlideTableCellId(next.id, newRow, anchorCol),
        rowSpan: newRs,
        colSpan: cs,
      },
    });
  }

  next.rowCount = prepared.rowCount + 1;
  next.cells = rebuildTableFromAnchors(next.id, next.rowCount, next.colCount, placed);
  next.rowHeightsFrac = [...prepared.rowHeightsFrac];
  if (insertAt === 0) {
    const h = next.rowHeightsFrac[0] * 0.5;
    next.rowHeightsFrac[0] = h;
    next.rowHeightsFrac.unshift(h);
  } else {
    const above = insertAt - 1;
    const h = next.rowHeightsFrac[above] * 0.5;
    next.rowHeightsFrac[above] = h;
    next.rowHeightsFrac.splice(insertAt, 0, h);
  }
  return normalizeTableGrid(next);
}

function insertTableColumnAt(table: ProgramSlideTableBlockV1, insertAt: number): ProgramSlideTableBlockV1 {
  if (table.colCount >= TABLE_MAX_COLS) return table;
  if (insertAt < 0 || insertAt > table.colCount) return table;

  const prepared = normalizeTableGrid(table);
  const next = cloneTableBlock(prepared);
  const placed: { row: number; col: number; cell: ProgramSlideTableCellV1 }[] = [];
  const processed = new Set<string>();

  for (const { row: anchorRow, col: anchorCol, cell } of collectTableAnchorSlots(prepared)) {
    if (processed.has(cell.id)) continue;
    processed.add(cell.id);
    const rs = Math.max(1, cell.rowSpan || 1);
    const cs = Math.max(1, cell.colSpan || 1);

    let newCol = anchorCol;
    let newCs = cs;
    if (anchorCol >= insertAt) {
      newCol = anchorCol + 1;
    } else if (anchorCol + cs > insertAt) {
      newCs = cs + 1;
    }

    placed.push({
      row: anchorRow,
      col: newCol,
      cell: {
        ...cell,
        id: genSlideTableCellId(next.id, anchorRow, newCol),
        rowSpan: rs,
        colSpan: newCs,
      },
    });
  }

  next.colCount = prepared.colCount + 1;
  next.cells = rebuildTableFromAnchors(next.id, next.rowCount, next.colCount, placed);
  next.colWidthsFrac = [...prepared.colWidthsFrac];
  if (insertAt === 0) {
    const w = next.colWidthsFrac[0] * 0.5;
    next.colWidthsFrac[0] = w;
    next.colWidthsFrac.unshift(w);
  } else {
    const left = insertAt - 1;
    const w = next.colWidthsFrac[left] * 0.5;
    next.colWidthsFrac[left] = w;
    next.colWidthsFrac.splice(insertAt, 0, w);
  }
  return normalizeTableGrid(next);
}

function deleteTableRowAt(table: ProgramSlideTableBlockV1, deleteAt: number): ProgramSlideTableBlockV1 {
  if (table.rowCount <= 1) return table;
  if (deleteAt < 0 || deleteAt >= table.rowCount) return table;

  const prepared = normalizeTableGrid(table);
  const placed: { row: number; col: number; cell: ProgramSlideTableCellV1 }[] = [];
  const processed = new Set<string>();

  for (const { row: anchorRow, col: anchorCol, cell } of collectTableAnchorSlots(prepared)) {
    if (processed.has(cell.id)) continue;
    processed.add(cell.id);
    const rs = Math.max(1, cell.rowSpan || 1);
    const cs = Math.max(1, cell.colSpan || 1);
    const spanRowEnd = anchorRow + rs - 1;

    if (anchorRow === deleteAt && rs === 1) continue;
    if (anchorRow > deleteAt) {
      placed.push({
        row: anchorRow - 1,
        col: anchorCol,
        cell: {
          ...cell,
          id: genSlideTableCellId(prepared.id, anchorRow - 1, anchorCol),
          rowSpan: rs,
          colSpan: cs,
        },
      });
      continue;
    }
    if (spanRowEnd < deleteAt) {
      placed.push({
        row: anchorRow,
        col: anchorCol,
        cell: { ...cell, rowSpan: rs, colSpan: cs },
      });
      continue;
    }

    const newRs = rs - 1;
    if (newRs < 1) continue;
    const newRow = anchorRow === deleteAt ? deleteAt : anchorRow;
    placed.push({
      row: newRow,
      col: anchorCol,
      cell: {
        ...cell,
        id: genSlideTableCellId(prepared.id, newRow, anchorCol),
        rowSpan: newRs,
        colSpan: cs,
      },
    });
  }

  const next = cloneTableBlock(prepared);
  next.rowCount = prepared.rowCount - 1;
  next.cells = rebuildTableFromAnchors(next.id, next.rowCount, next.colCount, placed);
  next.rowHeightsFrac = [...prepared.rowHeightsFrac];
  next.rowHeightsFrac.splice(deleteAt, 1);
  const rowSum = next.rowHeightsFrac.reduce((a, b) => a + b, 0) || 1;
  next.rowHeightsFrac = next.rowHeightsFrac.map((v) => v / rowSum);
  return normalizeTableGrid(next);
}

function deleteTableColumnAt(table: ProgramSlideTableBlockV1, deleteAt: number): ProgramSlideTableBlockV1 {
  if (table.colCount <= 1) return table;
  if (deleteAt < 0 || deleteAt >= table.colCount) return table;

  const prepared = normalizeTableGrid(table);
  const placed: { row: number; col: number; cell: ProgramSlideTableCellV1 }[] = [];
  const processed = new Set<string>();

  for (const { row: anchorRow, col: anchorCol, cell } of collectTableAnchorSlots(prepared)) {
    if (processed.has(cell.id)) continue;
    processed.add(cell.id);
    const rs = Math.max(1, cell.rowSpan || 1);
    const cs = Math.max(1, cell.colSpan || 1);
    const spanColEnd = anchorCol + cs - 1;

    if (anchorCol === deleteAt && cs === 1) continue;
    if (anchorCol > deleteAt) {
      placed.push({
        row: anchorRow,
        col: anchorCol - 1,
        cell: {
          ...cell,
          id: genSlideTableCellId(prepared.id, anchorRow, anchorCol - 1),
          rowSpan: rs,
          colSpan: cs,
        },
      });
      continue;
    }
    if (spanColEnd < deleteAt) {
      placed.push({
        row: anchorRow,
        col: anchorCol,
        cell: { ...cell, rowSpan: rs, colSpan: cs },
      });
      continue;
    }

    const newCs = cs - 1;
    if (newCs < 1) continue;
    const newCol = anchorCol === deleteAt ? deleteAt : anchorCol;
    placed.push({
      row: anchorRow,
      col: newCol,
      cell: {
        ...cell,
        id: genSlideTableCellId(prepared.id, anchorRow, newCol),
        rowSpan: rs,
        colSpan: newCs,
      },
    });
  }

  const next = cloneTableBlock(prepared);
  next.colCount = prepared.colCount - 1;
  next.cells = rebuildTableFromAnchors(next.id, next.rowCount, next.colCount, placed);
  next.colWidthsFrac = [...prepared.colWidthsFrac];
  next.colWidthsFrac.splice(deleteAt, 1);
  const colSum = next.colWidthsFrac.reduce((a, b) => a + b, 0) || 1;
  next.colWidthsFrac = next.colWidthsFrac.map((v) => v / colSum);
  return normalizeTableGrid(next);
}

export function getTableInsertRowIndex(rect: TableSelectionRect, position: 'above' | 'below'): number {
  const r = normalizeTableSelectionRect(rect);
  return position === 'above' ? r.rowStart : r.rowEnd;
}

export function getTableInsertColIndex(rect: TableSelectionRect, position: 'left' | 'right'): number {
  const r = normalizeTableSelectionRect(rect);
  return position === 'left' ? r.colStart : r.colEnd;
}

export function insertTableRow(
  table: ProgramSlideTableBlockV1,
  index: number,
  position: 'above' | 'below'
): ProgramSlideTableBlockV1 {
  const insertAt = position === 'above' ? index : index + 1;
  return insertTableRowAt(table, insertAt);
}

export function insertTableColumn(
  table: ProgramSlideTableBlockV1,
  index: number,
  position: 'left' | 'right'
): ProgramSlideTableBlockV1 {
  const insertAt = position === 'left' ? index : index + 1;
  return insertTableColumnAt(table, insertAt);
}

export function deleteTableRows(table: ProgramSlideTableBlockV1, indices: number[]): ProgramSlideTableBlockV1 {
  const sorted = Array.from(new Set(indices.filter((i) => i >= 0 && i < table.rowCount))).sort((a, b) => b - a);
  if (!sorted.length || table.rowCount - sorted.length < 1) return table;
  let next = table;
  for (const row of sorted) {
    next = deleteTableRowAt(next, row);
  }
  return next;
}

export function deleteTableColumns(table: ProgramSlideTableBlockV1, indices: number[]): ProgramSlideTableBlockV1 {
  const sorted = Array.from(new Set(indices.filter((i) => i >= 0 && i < table.colCount))).sort((a, b) => b - a);
  if (!sorted.length || table.colCount - sorted.length < 1) return table;
  let next = table;
  for (const col of sorted) {
    next = deleteTableColumnAt(next, col);
  }
  return next;
}

export function getSelectedRowIndices(rect: TableSelectionRect): number[] {
  const r = normalizeTableSelectionRect(rect);
  const out: number[] = [];
  for (let row = r.rowStart; row <= r.rowEnd; row += 1) out.push(row);
  return out;
}

export function getSelectedColIndices(rect: TableSelectionRect): number[] {
  const r = normalizeTableSelectionRect(rect);
  const out: number[] = [];
  for (let col = r.colStart; col <= r.colEnd; col += 1) out.push(col);
  return out;
}

export function getAnchorCellIdsInSelection(table: ProgramSlideTableBlockV1, rect: TableSelectionRect): string[] {
  const r = normalizeTableSelectionRect(rect);
  const ids = new Set<string>();
  for (let row = r.rowStart; row <= r.rowEnd; row += 1) {
    for (let col = r.colStart; col <= r.colEnd; col += 1) {
      const anchor = getTableAnchorCell(table, row, col);
      if (anchor) ids.add(anchor.cell.id);
    }
  }
  return Array.from(ids);
}

export function parseSlideTableBlock(raw: unknown, zFallback: number): ProgramSlideTableBlockV1 | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const rowCount = Math.min(TABLE_MAX_ROWS, Math.max(1, Math.round(Number(o.rowCount) || 1)));
  const colCount = Math.min(TABLE_MAX_COLS, Math.max(1, Math.round(Number(o.colCount) || 1)));
  const id = String(o.id || genSlideTableId(0));
  const cells: ProgramSlideTableCellV1[] = [];
  const rawCells = Array.isArray(o.cells) ? o.cells : [];
  for (let row = 0; row < rowCount; row += 1) {
    for (let col = 0; col < colCount; col += 1) {
      const idx = row * colCount + col;
      const c = rawCells[idx];
      if (!c || typeof c !== 'object') {
        cells.push(createDefaultTableCell(id, row, col));
        continue;
      }
      const co = c as Record<string, unknown>;
      cells.push({
        id: String(co.id || genSlideTableCellId(id, row, col)),
        text: String(co.text ?? ''),
        textAlign: normalizeTableTextAlign(co.textAlign),
        verticalAlign: normalizeTableVerticalAlign(co.verticalAlign),
        colorHex: normalizeHexColor(co.colorHex),
        backgroundColorHex: normalizeHexColor(co.backgroundColorHex, '#ffffff'),
        bold: !!co.bold,
        rowSpan: Math.max(1, Math.round(Number(co.rowSpan) || 1)),
        colSpan: Math.max(1, Math.round(Number(co.colSpan) || 1)),
      });
    }
  }
  const coveredBy: Record<string, string> = {};
  if (o.coveredBy && typeof o.coveredBy === 'object' && !Array.isArray(o.coveredBy)) {
    for (const [k, v] of Object.entries(o.coveredBy as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim()) coveredBy[k] = v.trim();
    }
  }
  const rowHeightsRaw = Array.isArray(o.rowHeightsFrac) ? o.rowHeightsFrac : [];
  const colWidthsRaw = Array.isArray(o.colWidthsFrac) ? o.colWidthsFrac : [];
  const groupId = parseSlideBlockGroupId(o.groupId);
  const borderW = Number(o.borderWidthPx);
  const parsed: ProgramSlideTableBlockV1 = {
    id,
    x: clamp01(Number(o.x)),
    y: clamp01(Number(o.y)),
    w: clampDim(Number(o.w)),
    h: clampDim(Number(o.h)),
    rowCount,
    colCount,
    cells,
    coveredBy,
    rowHeightsFrac:
      rowHeightsRaw.length === rowCount
        ? rowHeightsRaw.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
        : equalFracs(rowCount),
    colWidthsFrac:
      colWidthsRaw.length === colCount
        ? colWidthsRaw.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
        : equalFracs(colCount),
    borderColorHex: normalizeHexColor(o.borderColorHex, '#333333'),
    borderWidthPx: Number.isFinite(borderW) ? Math.min(8, Math.max(0, Math.round(borderW))) : TABLE_DEFAULT_BORDER_WIDTH_PX,
    defaultFontSizePx: Math.min(48, Math.max(8, Math.round(Number(o.defaultFontSizePx) || TABLE_DEFAULT_FONT_SIZE_PX))),
    defaultFontFamily: normalizeSlideTextFontFamily(o.defaultFontFamily),
    headerRow: !!o.headerRow,
    zIndex: Number.isFinite(Number(o.zIndex)) ? Number(o.zIndex) : zFallback,
    ...(groupId ? { groupId } : {}),
    ...(o.locked ? { locked: true } : {}),
  };
  return normalizeTableGrid(parsed);
}

export function tableBlockHasContent(table: ProgramSlideTableBlockV1): boolean {
  return (table.cells ?? []).some((c) => (c.text ?? '').trim().length > 0);
}
